import React, { useEffect, useState, useRef } from 'react';
import { Button, Flex, Text } from '@radix-ui/themes';

const GlobalSOSAlert = ({ sosAlert, onDismiss }) => {
  const [currentAudio, setCurrentAudio] = useState(null);
  const [stoppedAlarmAudios, setStoppedAlarmAudios] = useState([]);
  const audioRef = useRef(null);

  // Get the current logged-in user ID to suppress the alert for the triggering user
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    if (sosAlert) {
      stopAllAlarmAudio();
      playSosAudio();
    }

    return () => {
      stopSosAudio();
      if (sosAlert) {
        restoreAlarmAudio();
      }
    };
  }, [sosAlert]);

  const stopAllAlarmAudio = () => {
    try {
      const alarmAudioElements = document.querySelectorAll('audio[data-alarm-audio="true"]');
      const stoppedAudios = [];

      console.log(`🔇 Stopping ${alarmAudioElements.length} alarm audio elements for SOS`);

      alarmAudioElements.forEach((audio, index) => {
        if (!audio.paused) {
          stoppedAudios.push({
            element: audio,
            src: audio.src,
            currentTime: audio.currentTime,
            loop: audio.loop
          });
          audio.pause();
          console.log(`Paused alarm audio element ${index + 1}`);
        }
      });

      setStoppedAlarmAudios(stoppedAudios);
    } catch (error) {
      console.error('Error stopping alarm audio for SOS:', error);
    }
  };

  const restoreAlarmAudio = () => {
    try {
      console.log(`🔊 Restoring ${stoppedAlarmAudios.length} alarm audio elements after SOS`);

      stoppedAlarmAudios.forEach((audioInfo, index) => {
        try {
          const { element, currentTime, loop } = audioInfo;
          if (element && element.src) {
            element.currentTime = currentTime;
            element.loop = loop;
            element.play().then(() => {
              console.log(`Restored alarm audio element ${index + 1}`);
            }).catch(err => {
              console.error(`Failed to restore alarm audio element ${index + 1}:`, err);
            });
          }
        } catch (err) {
          console.error(`Error restoring individual alarm audio ${index + 1}:`, err);
        }
      });

      setStoppedAlarmAudios([]);
    } catch (error) {
      console.error('Error restoring alarm audio after SOS:', error);
    }
  };

  const playSosAudio = async () => {
    try {
      const dbRequest = indexedDB.open('AlarmAudioDB', 1);

      dbRequest.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['audioFiles'], 'readonly');
        const store = transaction.objectStore('audioFiles');
        const request = store.get('sos.mp3');

        request.onsuccess = (e) => {
          const audioBlob = e.target.result;
          if (audioBlob) {
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            audio.loop = true;
            audio.setAttribute('data-sos-audio', 'true');
            audioRef.current = audio;
            setCurrentAudio(audio);

            audio.play().then(() => {
              console.log('🆘 SOS audio playing');
            }).catch(err => {
              console.error('SOS audio playback error:', err);
            });

            audio.onended = () => {
              URL.revokeObjectURL(audioUrl);
            };
          } else {
            console.warn('SOS audio file not found in cache');
          }
        };

        request.onerror = (error) => {
          console.error('Error retrieving SOS audio:', error);
        };
      };

      dbRequest.onerror = (error) => {
        console.error('Error opening IndexedDB for SOS audio:', error);
      };
    } catch (error) {
      console.error('Error playing SOS audio:', error);
    }
  };

  const stopSosAudio = () => {
    if (currentAudio) {
      try {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio.src = '';
        currentAudio.load();
        setCurrentAudio(null);
        audioRef.current = null;
        console.log('🔇 SOS audio stopped');
      } catch (error) {
        console.error('Error stopping SOS audio:', error);
      }
    }
  };

  const handleDismiss = () => {
    stopSosAudio();
    restoreAlarmAudio();
    onDismiss();
  };

  if (!sosAlert) return null;

  const { payload } = sosAlert;

  // Handle both casing variations from the backend
  const CodeName   = payload?.CodeName   || payload?.codeName;
  const color      = payload?.color;
  const Meaning    = payload?.Meaning    || payload?.meaning;
  const Department = payload?.Department || payload?.department;
  const Details    = payload?.Details    || payload?.details;
  const triggerUserId = payload?.UserId  || payload?.userId;

  // Do NOT show the SOS alert to the user who triggered it
  if (triggerUserId && currentUserId && String(triggerUserId) === String(currentUserId)) {
    console.log('🆘 SOS triggered by current user — suppressing display for self');
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-80 backdrop-blur-sm"
        style={{ zIndex: 49 }}
      />

      {/* SOS Alert Content */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8 border-4"
        style={{
          borderColor: color || '#dc2626',
          zIndex: 50,
          animation: 'pulse-sos 1.2s ease-in-out infinite'
        }}
      >
        <style>
          {`
            @keyframes pulse-sos {
              0% {
                box-shadow: 0 0 0 0 ${color || '#dc2626'}60, 0 0 20px 4px ${color || '#dc2626'}80;
                transform: scale(1);
              }
              50% {
                box-shadow: 0 0 0 20px ${color || '#dc2626'}20, 0 0 40px 8px ${color || '#dc2626'}60;
                transform: scale(1.01);
              }
              100% {
                box-shadow: 0 0 0 0 ${color || '#dc2626'}00, 0 0 20px 4px ${color || '#dc2626'}80;
                transform: scale(1);
              }
            }
          `}
        </style>

        <Flex direction="column" align="center" gap="5">
          {/* Emergency Alert Header */}
          <div className="text-center">
            <Text size="7" weight="bold" className="text-red-600">
              EMERGENCY ALERT
            </Text>
          </div>

          {/* Code Display */}
          <div
            className="w-full p-6 rounded-xl text-center"
            style={{
              backgroundColor: `${color || '#dc2626'}15`,
              border: `3px solid ${color || '#dc2626'}`
            }}
          >
            <Flex direction="column" align="center" gap="3">
              <div
                className="w-16 h-16 rounded-full border-4 border-white shadow-lg"
                style={{ backgroundColor: color || '#dc2626' }}
              />
              <Text
                size="6"
                weight="bold"
                style={{ color: color || '#dc2626' }}
              >
                CODE {CodeName?.toUpperCase() || 'EMERGENCY'}
              </Text>
              {/* Code meaning — the plain-English label */}
              {Meaning && (
                <Text size="4" weight="medium" style={{ color: color || '#dc2626', opacity: 0.85 }}>
                  {Meaning}
                </Text>
              )}
            </Flex>
          </div>

          {/* Department & Location row */}
          {Department && (
            <div
              className="w-full px-4 py-3 rounded-lg flex items-center gap-3"
              style={{ backgroundColor: `${color || '#dc2626'}10`, border: `1px solid ${color || '#dc2626'}40` }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color || '#dc2626'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <div>
                <Text size="1" color="gray" className="block">Department / Location</Text>
                <Text size="3" weight="bold" style={{ color: color || '#dc2626' }}>
                  {Department}
                </Text>
              </div>
            </div>
          )}

          {/* Emergency Details */}
          {Details && (
            <div
              className="w-full p-4 bg-gray-50 rounded-lg border-l-4"
              style={{ borderColor: color || '#dc2626' }}
            >
              <Text size="2" weight="medium" className="text-gray-600 mb-1 block">
                Details:
              </Text>
              <Text size="3" className="text-gray-900 leading-relaxed">
                {Details}
              </Text>
            </div>
          )}

          {/* Dismiss Button */}
          <Button
            onClick={handleDismiss}
            size="4"
            className="w-full font-bold py-4 text-white"
            style={{
              backgroundColor: '#4b5563',
              fontSize: '16px',
              marginTop: '4px'
            }}
          >
            ACKNOWLEDGE & DISMISS
          </Button>

          {/* Timestamp */}
          <Text size="2" color="gray" className="text-center">
            Alerted at: {new Date().toLocaleTimeString()}
          </Text>
        </Flex>
      </div>
    </div>
  );
};

export default GlobalSOSAlert;